import { type MutableRefObject, startTransition, useCallback, useMemo } from 'react';
import { useIsMounted } from '../hooks/useIsMounted';
import { useRefUpdating } from '../hooks/useRefUpdating';
import { useAuthToken } from '../useMe';
import useWebSocket, { WS_BASE_URL } from '../useWebSocket2';
import type { Subscription } from '../ws/types';
import { injectIntoInfiniteData2 } from './infinite';
import {
  type InfiniteStore,
  type UseInfiniteRequestItem,
  type UseInfiniteRequestOpts,
  type UseRequestOpts,
  type WithNew,
  isNewDbItem,
} from './types';

// Between these hours, use the extra long MAX pause
const BUSINESS_HOURS_START = 5;
const BUSINESS_HOURS_END = 23;

const ptTime = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  hour: 'numeric',
  hour12: false,
  hourCycle: 'h23',
});

// Randomization between polling for every client
const JITTER = 500;

// Items request limit while polling
const POLL_LIMIT = 10;

// Default key to use while polling
const ID = 'id' as const;

const yyy = 0;

export function useNextInfiniteWebSocket<
  const ItemType extends API.DbItem,
  const BodyType = void,
  const QueryType extends API.BasePageInput = API.WithPageInput<ItemType>,
  const MetaType = void,
  const FromSocketType = ItemType,
>(
  isClientReady: boolean,
  key: string,
  opts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
  initialOpts: UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>,
  knownIdsRef: MutableRefObject<Set<API.Id>>,
  requestFetcher,
  basePayload,
  storeBank: UseInfiniteRequestItem<ItemType, MetaType>,
  storeGet: InfiniteStore<ItemType, MetaType>['get'],
  storeSet: InfiniteStore<ItemType, MetaType>['set'],
  storeRemove: InfiniteStore<ItemType, MetaType>['remove'],
  wsReadyRef: MutableRefObject<boolean>,
) {
  const isClientReadyRef = useRefUpdating(isClientReady);
  const eventValidateRef = useRefUpdating(opts?.eventValidate);
  const keyRef = useRefUpdating(key);
  const cursorKeyRef = useRefUpdating(opts?.cursorKey || ID);
  const queryRef = useRefUpdating(initialOpts?.query);

  const bank = storeBank?.data || basePayload?.data;
  const bankRef = useRefUpdating(bank);

  const storeSetRef = useRefUpdating(storeSet);

  const isMounted = useIsMounted();

  // Message changed updater
  // biome-ignore lint/correctness/useExhaustiveDependencies(knownIdsRef): React Ref
  // biome-ignore lint/correctness/useExhaustiveDependencies(knownIdsRef.current): React Ref
  // biome-ignore lint/correctness/useExhaustiveDependencies(knownIdsRef.current?.has): React Ref
  const onInfiniteUpdated = useMemo(
    () => {
      return async (event: MessageEvent, ojson: API.WebsocketResult<API.Id>) => {
        if (!isClientReadyRef.current) {
          return;
        }

        const curKey = keyRef.current;
        const rjson = (await eventValidateRef.current?.(event, ojson, knownIdsRef)) as
          | false
          | API.SubscriptionResponse<ItemType>;
        if (rjson === false) {
          return;
        }

        if (typeof rjson === 'number') {
          // Still a number, fetch from DB
          console.warn('Received update from WS but eventValidate did not fetch the data');

          return;
        }

        const json = rjson || ojson;

        const newItem = json?.data;
        const isInSet = knownIdsRef.current?.has?.(newItem.id);

        if (isInSet) {
          // We have this in our cache somewhere. Update it.
          startTransition(() => {
            storeSetRef.current?.(
              keyRef.current,
              (draft, original) => {
                const origDataSet = original || bankRef.current;
                injectIntoInfiniteData2(
                  [json?.data as ItemType],
                  origDataSet,
                  draft.data,
                  knownIdsRef,
                  cursorKeyRef.current,
                  1,
                  'never',
                  'merge',
                );
              },
              // { resetTimeCached: true, key: curKey, notify: isMounted.current },
            );
          });
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Message received injector
  // biome-ignore lint/correctness/useExhaustiveDependencies(knownIdsRef): React Ref
  // biome-ignore lint/correctness/useExhaustiveDependencies(knownIdsRef.current?.has): React Ref
  const onInfiniteCreated = useCallback<Subscription<WithNew<ItemType>, string, FromSocketType>>(
    async (event, ojson) => {
      if (!isClientReadyRef.current) {
        return;
      }

      if (isNewDbItem(ojson?.data)) {
        ojson.data.isNew = true;
      }

      const curKey = keyRef.current;
      const res = await eventValidateRef.current?.(event, ojson, knownIdsRef);

      if (res === false) {
        return;
      }

      const json = (res as API.SubscriptionResponse<ItemType>) || ojson;

      startTransition(() => {
        storeSetRef.current?.(
          keyRef.current,
          (draft, original) => {
            const origDataSet = original || bankRef.current;
            injectIntoInfiniteData2(
              [json?.data as ItemType],
              origDataSet,
              draft.data,
              knownIdsRef,
              cursorKeyRef.current,
              1,
              queryRef.current?.order?.dir === 'DESC' ? 'start' : 'end',
            );
          },
          // { resetTimeCached: true, key: curKey, notify: isMounted.current },
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const authToken = useAuthToken();

  const ws = useWebSocket(
    WS_BASE_URL,
    useMemo(
      () => ({
        connect: !!opts?.event && !!authToken,
        subscriptions: opts?.event
          ? {
              [`${opts?.event}/updated`]: onInfiniteUpdated,
              [`${opts?.event}/created`]: onInfiniteCreated,
            }
          : undefined,
      }),
      [onInfiniteUpdated, onInfiniteCreated, opts?.event, authToken],
    ),
  );

  return ws;
}
